import React, { useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { Card, CardBody } from "reactstrap";
import { ColumnDef } from "@tanstack/react-table";
import TableContainer from "components/common/TableContainer";
import StatusBadge from "components/common/StatusBadge";
import { IndexColumn } from "components/common/columns";
import { DateTime } from "components/common/Date";
import { getMaterialRequests } from "features/MaterialRequests/services/MaterialRequestService";
import type { MaterialRequest } from "features/MaterialRequests/types/material-request.types";
import {
  MaterialRequestSourceType,
  MaterialRequestStatusMap,
} from "features/MaterialRequests/constants/material-request.constants";
import { getMaterialRequestFromJobPath } from "features/MaterialRequests/utils/materialRequestFromJobPaths";
import { materialRequestDisplayCode } from "features/MaterialRequests/utils/material-request.utils";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import { usePettyWorkOverview } from "./PettyWorkOverviewContext";
import { pettyWorkAllowsMaterialRequest } from "features/PettyWorks/utils/pettyWorkActionPolicy";

export default function MaterialRequestsTab() {
  const { pettyWork } = usePettyWorkOverview();
  const { can } = useAccess();
  const canRead = can(Permissions.MATERIAL_REQUEST.READ);
  const canCreate =
    can(Permissions.MATERIAL_REQUEST.CREATE) &&
    pettyWorkAllowsMaterialRequest(pettyWork?.status);
  const tableRef = useRef<() => void>(null);

  const sourceId = pettyWork?.id;

  const requestParams = useMemo(
    () => ({
      sourceType: MaterialRequestSourceType.PETTY_WORK,
      sourceId: sourceId ?? 0,
    }),
    [sourceId],
  );

  const columns: ColumnDef<MaterialRequest>[] = useMemo(
    () => [
      IndexColumn<MaterialRequest>(),
      {
        header: "Code",
        id: "code",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const code = materialRequestDisplayCode(row.original);
          return canRead ? (
            <Link
              to={`/inventory-management/material-requests/${row.original.id}`}
              className="fw-medium link"
            >
              {code}
            </Link>
          ) : (
            <span className="fw-medium">{code}</span>
          );
        },
      },
      {
        header: "Status",
        id: "status",
        enableColumnFilter: false,
        cell: ({ row }) => (
          <StatusBadge
            value={row.original.status}
            map={MaterialRequestStatusMap}
          />
        ),
      },
      {
        header: "Requested on",
        id: "createdAt",
        enableColumnFilter: false,
        cell: ({ row }) =>
          row.original.created_at ? (
            <DateTime date={row.original.created_at} />
          ) : (
            <span className="text-muted">—</span>
          ),
      },
    ],
    [canRead],
  );

  if (sourceId == null) return null;

  return (
    <Card>
      <CardBody>
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
          <h6 className="mb-0 text-uppercase">Material Requests</h6>
          {canCreate ? (
            <Link
              to={getMaterialRequestFromJobPath(
                MaterialRequestSourceType.PETTY_WORK,
                sourceId,
              )}
              className="btn btn-primary btn-sm btn-label"
            >
              <i className="ri-add-line label-icon align-middle fs-16 me-1" />
              Request materials
            </Link>
          ) : null}
        </div>
        <TableContainer
          columns={columns}
          fetchApi={getMaterialRequests}
          requestParams={requestParams}
          refreshRef={tableRef}
          syncPaginationToUrl={false}
          pageSize={10}
          globalSearch={false}
          divClass="table-responsive table-card mb-0"
          tableClass="align-middle table-nowrap mb-0"
          theadClass="table-light text-muted"
        />
      </CardBody>
    </Card>
  );
}
